'use client'
import { StoreState } from '@/domain/state/index'
import { Token } from '@/domain/types/Token'
import { StateCreator } from 'zustand'
import { RiskLevel } from '../common/risk'

export enum SideDrawerTabs {
  TOKENS = 'Tokens',
  POSITIONS = 'Positions',
}

export interface SideDrawer {
  activeTab: SideDrawerTabs
  isOpen: boolean
  swapDialog: {
    isOpen: boolean
    defaultToToken?: Token
    setOpen: (isOpen: boolean) => void
    open: (defaultToToken?: Token) => void
  }
  tokensTab: {
    isSupportedOpen: boolean
    isOtherOpen: boolean
    setIsSupportedOpen: (isSupportedOpen: boolean) => void
    setIsOtherOpen: (isOtherOpen: boolean) => void
  }
  positionsTab: {
    isPooledOpen: boolean
    isIsolatedOpen: boolean
    isEulerOpen: boolean
    showPositionsBadge: boolean
    highestRiskLevel: RiskLevel
    setHighestRiskLevel: (highestRiskLevel: RiskLevel) => void
    setShowPositionsBadge: (showPositionsBadge: boolean) => void
    setIsPooledOpen: (isPooledOpen: boolean) => void
    setIsIsolatedOpen: (isIsolatedOpen: boolean) => void
    setIsEulerOpen: (isEulerOpen: boolean) => void
  }
  setActiveTab: (activeTab: SideDrawerTabs) => void
  setOpen: (isOpen: boolean) => void
  open: () => void
  close: () => void
  toggle: () => void
}

export interface SideDrawerSlice {
  sideDrawer: SideDrawer
}

export const initSideDrawerSlice: StateCreator<StoreState, [], [], SideDrawerSlice> = (set) => ({
  sideDrawer: {
    activeTab: SideDrawerTabs.TOKENS,
    isOpen: false,
    swapDialog: {
      isOpen: false,
      setOpen: (isOpen: boolean) => {
        set((state) => ({
          sideDrawer: {
            ...state.sideDrawer,
            swapDialog: {
              ...state.sideDrawer.swapDialog,
              isOpen,
              defaultToToken: isOpen ? state.sideDrawer.swapDialog.defaultToToken : undefined,
            },
          },
        }))
      },
      open: (defaultToToken?: Token) => {
        set((state) => ({
          sideDrawer: {
            ...state.sideDrawer,
            swapDialog: {
              ...state.sideDrawer.swapDialog,
              isOpen: true,
              defaultToToken,
            },
          },
        }))
      },
    },
    tokensTab: {
      isSupportedOpen: true,
      isOtherOpen: false,
      setIsSupportedOpen: (isSupportedOpen: boolean) =>
        set((state) => ({
          sideDrawer: {
            ...state.sideDrawer,
            tokensTab: {
              ...state.sideDrawer.tokensTab,
              isSupportedOpen,
            },
          },
        })),
      setIsOtherOpen: (isOtherOpen: boolean) =>
        set((state) => ({
          sideDrawer: {
            ...state.sideDrawer,
            tokensTab: {
              ...state.sideDrawer.tokensTab,
              isOtherOpen,
            },
          },
        })),
    },
    positionsTab: {
      isPooledOpen: true,
      isIsolatedOpen: true,
      isEulerOpen: true,
      showPositionsBadge: true,
      highestRiskLevel: 'unknown',
      setHighestRiskLevel: (highestRiskLevel: RiskLevel) =>
        set((state) => ({
          sideDrawer: {
            ...state.sideDrawer,
            positionsTab: { ...state.sideDrawer.positionsTab, highestRiskLevel },
          },
        })),
      setShowPositionsBadge: (showPositionsBadge: boolean) =>
        set((state) => ({
          sideDrawer: {
            ...state.sideDrawer,
            positionsTab: {
              ...state.sideDrawer.positionsTab,
              showPositionsBadge,
            },
          },
        })),
      setIsPooledOpen: (isPooledOpen: boolean) =>
        set((state) => ({
          sideDrawer: {
            ...state.sideDrawer,
            positionsTab: {
              ...state.sideDrawer.positionsTab,
              isPooledOpen,
            },
          },
        })),
      setIsIsolatedOpen: (isIsolatedOpen: boolean) =>
        set((state) => ({
          sideDrawer: {
            ...state.sideDrawer,
            positionsTab: {
              ...state.sideDrawer.positionsTab,
              isIsolatedOpen,
            },
          },
        })),
      setIsEulerOpen: (isEulerOpen: boolean) =>
        set((state) => ({
          sideDrawer: {
            ...state.sideDrawer,
            positionsTab: {
              ...state.sideDrawer.positionsTab,
              isEulerOpen,
            },
          },
        })),
    },
    setActiveTab: (activeTab: SideDrawerTabs) => {
      set((state) => ({
        sideDrawer: {
          ...state.sideDrawer,
          activeTab,
        },
      }))
    },
    setOpen: (isOpen: boolean) => {
      set((state) => ({
        sideDrawer: {
          ...state.sideDrawer,
          isOpen,
        },
      }))
    },
    open: () => {
      set((state) => ({
        sideDrawer: {
          ...state.sideDrawer,
          isOpen: true,
        },
      }))
    },
    close: () => {
      set((state) => ({
        sideDrawer: {
          ...state.sideDrawer,
          isOpen: false,
        },
      }))
    },
    toggle: () => {
      set((state) => ({
        sideDrawer: {
          ...state.sideDrawer,
          isOpen: !state.sideDrawer.isOpen,
        },
      }))
    },
  },
})

export interface PersistedSideDrawerSlice {
  sideDrawer: {
    positionsTab: {
      showPositionsBadge: boolean
      highestRiskLevel: RiskLevel
    }
  }
}

export function persistSideDrawerSlice(state: StoreState): PersistedSideDrawerSlice {
  return {
    sideDrawer: {
      positionsTab: {
        showPositionsBadge: state.sideDrawer.positionsTab.showPositionsBadge,
        highestRiskLevel: state.sideDrawer.positionsTab.highestRiskLevel,
      },
    },
  }
}
